import { Box } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import type { ProjectsListItemDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { MODULE_TYPE_CONFIG } from "Modules/Utils/moduleTypeConfig";
import { DetailActionCell } from "Shared/DataTable/DetailActionCell";
import { EntityLink } from "Shared/EntityLink";
import { NameLinkCell } from "Shared/NameLinkCell";
import { StatusChip } from "Shared/StatusChip";

const columnHelper = createColumnHelper<ProjectsListItemDto>();

export const PROJECTS_SORTABLE_COLUMNS = [
  "name",
  "clientName",
  "projectState",
] as const;

const isSortable = (id: string) =>
  (PROJECTS_SORTABLE_COLUMNS as readonly string[]).includes(id);

export function useProjectsTableColumns() {
  const { t } = useResource();

  return useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(Resources.Dashboard.ProjectName),
        meta: { fillRow: true },
        enableSorting: isSortable("name"),
        cell: ({ row }) => {
          const config = MODULE_TYPE_CONFIG[row.original.moduleType];
          return (
            <NameLinkCell
              path={AppRouting.ProjectDetail.path(row.original.projectID!)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    color: config.color,
                    "& svg": { fontSize: 24 },
                  }}
                >
                  {config.icon}
                </Box>
                {row.original.name}
              </Box>
            </NameLinkCell>
          );
        },
      }),
      columnHelper.accessor("clientName", {
        header: t(Resources.Dashboard.Client),
        meta: { fillRow: true },
        enableSorting: isSortable("clientName"),
        cell: ({ row }) => {
          if (!row.original.clientID) {
            return <Box>-</Box>;
          }
          return (
            <EntityLink
              label={row.original.clientName ?? "-"}
              path={AppRouting.ClientDetail.path(row.original.clientID)}
            />
          );
        },
      }),
      columnHelper.display({
        id: "license",
        header: t(Resources.Dashboard.License),
        cell: (ctx) => ctx.row.original.licensedCountries.join(", "),
        enableSorting: false,
      }),
      columnHelper.display({
        id: "jobs",
        header: t(Resources.Dashboard.Jobs),
        enableSorting: false,
        cell: ({ row }) => {
          const { jobsCount, jobsLimit } = row.original;
          return jobsLimit > 0 ? `${jobsCount} / ${jobsLimit}` : jobsCount;
        },
      }),
      columnHelper.accessor("projectState", {
        header: t(Resources.Dashboard.Status),
        cell: (ctx) => <StatusChip projectState={ctx.getValue()} />,
        meta: { maxWidth: 160 },
        enableSorting: isSortable("projectState"),
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: ({ row }) => (
          <DetailActionCell
            path={AppRouting.ProjectDetail.path(row.original.projectID)}
          />
        ),
      }),
    ],
    [t],
  );
}
